import React, { useEffect, useMemo, useState } from 'react';
import type { UserProfile } from '../types';
import { subscribeToPublicUserProfile } from '../services/users';

type Props = {
  userId: string;
  fallbackName?: string;
  fallbackAvatar?: string | null;
  onClose: () => void;
  onViewProfile?: (userId: string) => void;
};

const DEFAULT_AVATAR = '/avatar-default.png';

const safeNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const pickAvatar = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return DEFAULT_AVATAR;
};

export default function PublicAnglerPreview({
  userId,
  fallbackName = 'Roddo Angler',
  fallbackAvatar = null,
  onClose,
  onViewProfile,
}: Props) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setProfile(null);

    return subscribeToPublicUserProfile(
      userId,
      (next) => {
        setProfile(next);
        setLoading(false);
      },
      (error) => {
        console.warn('[PublicAnglerPreview] public profile unavailable', error);
        setLoading(false);
      },
    );
  }, [userId]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const displayName = String(profile?.name || profile?.displayName || fallbackName || 'Roddo Angler').trim() || 'Roddo Angler';
  const username = String((profile as any)?.username || (profile as any)?.handle || '').trim();
  const avatar = pickAvatar(
    (profile as any)?.avatar,
    (profile as any)?.avatarUrl,
    (profile as any)?.photoURL,
    fallbackAvatar,
  );
  const level = Math.max(1, safeNumber((profile as any)?.level, 1));
  const xp = Math.max(0, safeNumber((profile as any)?.xp ?? (profile as any)?.points, 0));
  const canShowStats = (profile as any)?.publicProfileVisibility?.showStats !== false;

  const stats = useMemo(() => {
    const raw = (profile as any)?.stats || {};
    return {
      catches: Math.max(
        0,
        safeNumber(
          raw?.catches ?? raw?.totalCatches ?? raw?.catchCount ?? raw?.catchesCount ?? (profile as any)?.totalCatches,
          0,
        ),
      ),
      species: Math.max(
        0,
        safeNumber(
          raw?.species ?? raw?.speciesCount ?? raw?.uniqueSpecies ?? raw?.uniqueSpeciesCount ?? (profile as any)?.speciesCount,
          0,
        ),
      ),
      waters: Math.max(
        0,
        safeNumber(
          raw?.waters ?? raw?.spots ?? raw?.locations ?? raw?.uniqueWaters ?? raw?.uniqueWatersCount ?? (profile as any)?.spotsCount,
          0,
        ),
      ),
    };
  }, [profile]);

  return (
    <div
      className="roddo-safe-overlay fixed inset-0 z-[1300] grid place-items-center overflow-y-auto bg-black/60 p-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${displayName} profile preview`}
        className="roddo-safe-modal-frame w-full max-w-sm overflow-y-auto rounded-[22px] border border-black/10 bg-[#FFF9EF] text-slate-900 shadow-[0_24px_72px_rgba(0,0,0,.30)] dark:border-white/[.10] dark:bg-[#111A2B] dark:text-slate-100"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-black/[.07] px-4 py-4 dark:border-white/[.08]">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={avatar}
              alt=""
              className="h-14 w-14 shrink-0 rounded-full border border-black/[.08] object-cover shadow-sm dark:border-white/[.10]"
            />
            <div className="min-w-0">
              <div className="truncate text-[17px] font-semibold">{displayName}</div>
              {username ? <div className="mt-0.5 truncate text-xs font-medium opacity-55">@{username}</div> : null}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="rounded-full bg-[#2F80ED]/10 px-2.5 py-1 text-[10px] font-semibold text-[#2168C9] dark:bg-[#7057E8]/15 dark:text-[#CFC7FF]">
                  Level {level}
                </span>
                <span className="rounded-full bg-black/[.04] px-2.5 py-1 text-[10px] font-semibold opacity-70 dark:bg-white/[.06]">
                  {xp.toLocaleString()} XP
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-black/[.08] text-lg font-semibold opacity-65 transition hover:bg-black/[.04] dark:border-white/[.10] dark:hover:bg-white/[.06]"
            aria-label="Close profile preview"
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="px-4 py-4">
          {loading ? (
            <div className="rounded-[15px] bg-black/[.03] px-3 py-4 text-center text-sm font-medium opacity-55 dark:bg-white/[.04]">
              Loading angler…
            </div>
          ) : canShowStats ? (
            <div className="grid grid-cols-3 gap-2">
              {[
                ['Catches', stats.catches],
                ['Species', stats.species],
                ['Waters', stats.waters],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-[14px] border border-black/[.07] bg-black/[.025] px-2 py-3 text-center dark:border-white/[.08] dark:bg-white/[.04]">
                  <div className="text-[17px] font-semibold tabular-nums">{Number(value).toLocaleString()}</div>
                  <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[.09em] opacity-45">{label}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[15px] bg-black/[.03] px-3 py-4 text-center text-xs font-medium opacity-55 dark:bg-white/[.04]">
              This angler keeps their stats private.
            </div>
          )}

          {onViewProfile ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                onViewProfile(userId);
              }}
              className="mt-4 w-full rounded-[14px] bg-[#2F80ED] px-4 py-3 text-sm font-semibold text-white shadow-[0_7px_18px_rgba(47,128,237,.18)] transition active:scale-[.99] dark:bg-[#7057E8]"
            >
              View full profile
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
